// Горната лента на листа „Изписване" (`WorksheetTopBand`, както при
// доставката): назад · вид · заглавие и реквизитите на един ред — Дата · Склад
// · Вид (Изписване / Бракуване) · Причина (при изписване — свободен текст с
// подсказки от предишните причини, `WriteoffReasonField`; при бракуване —
// чипове `WRITEOFF_REASONS`, задължителни) · Коментар.
// Автоматичният брак от Маркиране е залостен: видът и запазената причина не се
// сменят.
//
// Видът и причините са истински radio бутони (скрити, стилизиран етикет) —
// както antd Segmented преди тях: стрелките сменят избора, Esc от тях води към
// търсачката (общият `useWorksheetKeyboard` ги вижда като поле), а буквите не
// „изтичат" към машината за писане.
import type { ComponentProps } from "react";
import { DatePicker, Input, Select } from "antd";
import { LockOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import type { Warehouse } from "../../../../api/inventory";
import { withCurrentTime } from "../../components/dateTime";
import { WorksheetField, WorksheetTopBand } from "../../components/worksheet/WorksheetTopBand";
import { WriteoffReasonField } from "./WriteoffReasonField";
import {
  AUTO_SCRAP_REASON,
  WRITEOFF_REASONS,
  type WriteoffKind,
} from "./writeoffWorksheetShared";

type Props = {
  // назад · заглавие · хапче за вида
  band: Omit<ComponentProps<typeof WorksheetTopBand>, "children">;
  date: Dayjs | null;
  onDateChange: (date: Dayjs | null) => void;
  warehouseId: number | null;
  onWarehouseChange: (warehouseId: number) => void;
  warehouses: Warehouse[];
  kind: WriteoffKind;
  onKindChange: (kind: WriteoffKind) => void;
  // Редакция на автоматичен брак от Маркиране → видът и причината са залостени
  autoScrapLocked: boolean;
  reason: string;
  reasonError: boolean;
  onReasonChange: (reason: string) => void;
  comment: string;
  onCommentChange: (comment: string) => void;
};

// Избраният чип: кехлибарен за изписване, червеникав за брак (като значката)
const CHECKED_CLASS: Record<WriteoffKind, string> = {
  writeoff: "border-brand bg-brand-tint font-semibold text-brand-deep-ink",
  scrap: "border-danger bg-danger-tint font-semibold text-danger-ink",
};

export const WriteoffWorksheetHeader = ({
  band,
  date,
  onDateChange,
  warehouseId,
  onWarehouseChange,
  warehouses,
  kind,
  onKindChange,
  autoScrapLocked,
  reason,
  reasonError,
  onReasonChange,
  comment,
  onCommentChange,
}: Props) => {
  const { t } = useTranslation();
  return (
    <WorksheetTopBand {...band}>
      <WorksheetField label={t("inventoryPage.fieldWriteoffDate")} size="sm">
        <DatePicker
          value={date}
          // antd връща 00:00 → прикачаме текущия час; при редакция запазеният
          // час се пази, докато датата не се смени ръчно
          onChange={(d) => onDateChange(withCurrentTime(d))}
          format="DD.MM.YYYY"
          allowClear={false}
          className="w-full"
        />
      </WorksheetField>
      <WorksheetField label={t("inventoryPage.fieldWarehouse")}>
        <Select
          value={warehouseId ?? undefined}
          onChange={(v) => onWarehouseChange(v as number)}
          placeholder={t("inventoryPage.fieldWarehousePh")}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          className="w-full"
        />
      </WorksheetField>
      <WorksheetField label={t("inventoryPage.colType")} size="auto">
        <RadioChips
          name="wo-kind"
          value={kind}
          disabled={autoScrapLocked}
          fill
          options={(["writeoff", "scrap"] as const).map((value) => ({
            value,
            label: t(value === "scrap" ? "inventoryPage.scrap" : "inventoryPage.writeoff"),
            checkedClass: CHECKED_CLASS[value],
          }))}
          onChange={(value) => onKindChange(value as WriteoffKind)}
        />
      </WorksheetField>
      {/* Изписване → свободен текст, по избор, с подсказки от предишните причини */}
      {kind === "writeoff" && !autoScrapLocked && (
        <WorksheetField label={t("inventoryPage.fieldReason")}>
          <WriteoffReasonField
            value={reason}
            onChange={onReasonChange}
            placeholder={t("inventoryPage.fieldReasonPh")}
          />
        </WorksheetField>
      )}
      {/* Бракуване → предефинирана причина, ЗАДЪЛЖИТЕЛНА (без „Друг");
          автоматичният брак от Маркиране показва запазената си причина */}
      {(kind === "scrap" || autoScrapLocked) && (
        <WorksheetField label={t("inventoryPage.fieldReason")} required={!autoScrapLocked} size="auto">
          {autoScrapLocked ? (
            <RadioChips
              name="wo-scrap-reason"
              value={AUTO_SCRAP_REASON}
              disabled
              options={[
                {
                  value: AUTO_SCRAP_REASON,
                  label: t("inventoryPage.scrapReasonVoidMarking"),
                  checkedClass: CHECKED_CLASS.scrap,
                  icon: <LockOutlined className="text-[12px]" />,
                },
              ]}
              onChange={() => {}}
            />
          ) : (
            <RadioChips
              name="wo-scrap-reason"
              value={reason}
              error={reasonError}
              options={WRITEOFF_REASONS.map((option) => ({
                value: option.value,
                label: t(`inventoryPage.${option.key}`),
                checkedClass: CHECKED_CLASS.scrap,
              }))}
              onChange={onReasonChange}
            />
          )}
        </WorksheetField>
      )}
      <WorksheetField label={t("inventoryPage.fieldComment")} size="grow">
        <Input
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder={t("inventoryPage.fieldCommentPh")}
          className="w-full"
        />
      </WorksheetField>
    </WorksheetTopBand>
  );
};

// Група radio бутони като чипове (`fill` → равни части по цялата ширина)
function RadioChips({
  name,
  value,
  options,
  onChange,
  disabled = false,
  error = false,
  fill = false,
}: {
  name: string;
  value: string;
  options: { value: string; label: string; checkedClass: string; icon?: React.ReactNode }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  fill?: boolean;
}) {
  return (
    <div role="radiogroup" aria-invalid={error || undefined} className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <label
            key={option.value}
            className={`relative inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border-[1.5px] px-3 text-[13px] leading-[normal] has-[:focus-visible]:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
              fill ? "flex-1" : ""
            } ${
              checked
                ? option.checkedClass
                : `bg-surface text-body ${error ? "border-danger/60" : "border-shade-3"} ${
                    disabled ? "" : "hover:border-shade-5"
                  }`
            } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.icon}
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

export default WriteoffWorksheetHeader;
